import { useState } from 'react';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { useDashboardAdmin } from './hooks/useDashboard.js';
import { KpiCard } from './components/KpiCard.js';
import { PeriodoSelector } from './components/PeriodoSelector.js';
import { VendasHistoricoChart } from './components/VendasHistoricoChart.js';
import { VendasDiaChart } from './components/VendasDiaChart.js';
import { TopClientesChart } from './components/TopClientesChart.js';
import { VendasVendedorChart } from './components/VendasVendedorChart.js';
import { BrasilMapaChart } from './components/BrasilMapaChart.js';
import { DashboardErrorBoundary } from './components/DashboardErrorBoundary.js';
import type { DashboardPeriodo } from './hooks/useDashboard.js';

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// AIDEV-NOTE: Dashboard admin — todos os vendedores + gráfico por vendedor + sync status
export function DashboardAdmin() {
  const agora = new Date();
  const [periodo, setPeriodo] = useState<DashboardPeriodo>({
    mes: agora.getMonth() + 1,
    ano: agora.getFullYear()
  });

  const { data, isLoading, error } = useDashboardAdmin(periodo);

  const mesLabel = `${MESES[periodo.mes - 1]}/${periodo.ano}`;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Erro ao carregar dashboard administrativo.
      </Typography>
    );
  }

  const visitasKpi = {
    label: 'Visitas no mês',
    value: data.visitasMes,
    unit: 'number' as const
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Cabeçalho com seletor de período */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Dashboard Administrativo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Todos os vendedores — {mesLabel}
          </Typography>
        </Box>
        <PeriodoSelector periodo={periodo} onChange={setPeriodo} />
      </Box>

      {/* KPIs de vendas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {data.kpis.map((kpi) => (
          <Grid item xs={6} sm={4} md={3} key={kpi.label}>
            <DashboardErrorBoundary>
              <KpiCard metric={kpi} />
            </DashboardErrorBoundary>
          </Grid>
        ))}
        <Grid item xs={6} sm={4} md={3}>
          <DashboardErrorBoundary>
            <KpiCard metric={visitasKpi} />
          </DashboardErrorBoundary>
        </Grid>
      </Grid>

      {/* Gráficos de vendas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <DashboardErrorBoundary fallbackMessage="Erro ao renderizar histórico">
            <VendasHistoricoChart dados={data.vendasHistorico} />
          </DashboardErrorBoundary>
        </Grid>

        <Grid item xs={12}>
          <DashboardErrorBoundary fallbackMessage="Erro ao renderizar gráfico diário">
            <VendasDiaChart dados={data.vendasDia} mesLabel={mesLabel} />
          </DashboardErrorBoundary>
        </Grid>

        <Grid item xs={12} md={6}>
          <DashboardErrorBoundary fallbackMessage="Erro ao renderizar top clientes">
            <TopClientesChart dados={data.topClientes} />
          </DashboardErrorBoundary>
        </Grid>

        <Grid item xs={12} md={6}>
          <DashboardErrorBoundary fallbackMessage="Erro ao renderizar vendas por vendedor">
            <VendasVendedorChart dados={data.vendasPorVendedor} />
          </DashboardErrorBoundary>
        </Grid>

        <Grid item xs={12}>
          <DashboardErrorBoundary fallbackMessage="Erro ao renderizar mapa de vendas">
            <BrasilMapaChart periodo={periodo} />
          </DashboardErrorBoundary>
        </Grid>
      </Grid>
    </Box>
  );
}
